import { WeekStartsOnDay } from "@gettactic/api";
import React from "react";

/** Fallback week start day (Monday), matching the historical hardcoded behavior. */
export const DEFAULT_WEEK_STARTS_ON: WeekStartsOnDay = 1;

const WeekStartsOnContext = React.createContext<WeekStartsOnDay>(
  DEFAULT_WEEK_STARTS_ON
);

export interface WeekStartsOnProviderProps {
  value: WeekStartsOnDay;
  children: React.ReactNode;
}

export const WeekStartsOnProvider: React.FC<WeekStartsOnProviderProps> = ({
  value,
  children
}) => (
  <WeekStartsOnContext.Provider value={value}>
    {children}
  </WeekStartsOnContext.Provider>
);

/**
 * Day calendar weeks start on for the current user (0=Sunday..6=Saturday).
 * Resolved from the user override falling back to the organization setting;
 * defaults to Monday when rendered outside the provider.
 */
export const useWeekStartsOn = (): WeekStartsOnDay =>
  React.useContext(WeekStartsOnContext);

/**
 * Resolves the effective week start day from the user override (null/undefined
 * means "inherit") and the organization-level setting.
 */
export const resolveWeekStartsOn = (
  userValue: number | null | undefined,
  organizationValue: number | null | undefined
): WeekStartsOnDay => {
  const resolved = userValue ?? organizationValue ?? DEFAULT_WEEK_STARTS_ON;
  if (Number.isInteger(resolved) && resolved >= 0 && resolved <= 6) {
    return resolved as WeekStartsOnDay;
  }
  return DEFAULT_WEEK_STARTS_ON;
};
